/**
 * Defensive parsers for SSE tool_call results from the three new GHL tools
 * (ghl_search_contact, ghl_get_messages, ghl_list_conversations).
 *
 * Same style as parseAvailabilityToolResult.ts / parseBookingToolResult.ts:
 *   - Never throws.
 *   - Unknown / malformed payloads return null rather than crashing the card.
 *   - Field coercion keeps the component code simple (no guards there).
 */

import type {
  GhlContactResultsAttachment,
  GhlContactResult,
  GhlConversationData,
  GhlConversationMessage,
  GhlInboxData,
  GhlInboxConversation,
} from '../types';

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function strArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

// ── ghl_search_contact ────────────────────────────────────────────────────────

/**
 * Parse `data.result` from SSE tool_call for ghl_search_contact.
 * Returns null if the payload is absent, malformed, or empty.
 */
export function parseContactSearchResult(
  resultStr: string
): GhlContactResultsAttachment | null {
  try {
    const raw = JSON.parse(resultStr);
    if (!isRecord(raw)) return null;

    const rawContacts = Array.isArray(raw.contacts) ? raw.contacts : [];
    if (rawContacts.length === 0) {
      // Return an empty list rather than null so the card can show "No contacts found."
      return { contacts: [] };
    }

    const contacts: GhlContactResult[] = rawContacts
      .filter(isRecord)
      .map((c) => ({
        contact_id: str(c.contact_id),
        name: str(c.name),
        email: str(c.email),
        phone: str(c.phone),
        tags: strArr(c.tags),
        date_added: str(c.date_added),
      }));

    return { contacts };
  } catch {
    return null;
  }
}

// ── ghl_get_messages ──────────────────────────────────────────────────────────

/**
 * Parse `data.result` from SSE tool_call for ghl_get_messages.
 * Returns null if the payload is absent or malformed. An empty message
 * list with a contact_name is still a valid (renderable) result.
 */
export function parseGetMessagesResult(
  resultStr: string
): GhlConversationData | null {
  try {
    const raw = JSON.parse(resultStr);
    if (!isRecord(raw)) return null;

    const contactName = str(raw.contact_name);
    const rawMessages = Array.isArray(raw.messages) ? raw.messages : [];

    const messages: GhlConversationMessage[] = rawMessages
      .filter(isRecord)
      .map((m) => {
        const dir = str(m.direction);
        const direction: GhlConversationMessage['direction'] =
          dir === 'inbound' || dir === 'outbound' ? dir : 'unknown';
        return {
          direction,
          type: str(m.type) || 'unknown',
          body: str(m.body),
          date_sent: str(m.date_sent),
          contact_name: contactName,
        };
      });

    return { messages, contact_name: contactName };
  } catch {
    return null;
  }
}

// ── ghl_list_conversations ────────────────────────────────────────────────────

/**
 * Parse `data.result` from SSE tool_call for ghl_list_conversations.
 * Returns null if the payload is absent or malformed.
 */
export function parseListConversationsResult(
  resultStr: string
): GhlInboxData | null {
  try {
    const raw = JSON.parse(resultStr);
    if (!isRecord(raw)) return null;

    const rawConversations = Array.isArray(raw.conversations) ? raw.conversations : [];

    const conversations: GhlInboxConversation[] = rawConversations
      .filter(isRecord)
      .map((c) => ({
        conversation_id: str(c.conversation_id),
        contact_name: str(c.contact_name),
        contact_id: str(c.contact_id),
        last_message_preview: str(c.last_message_preview),
        last_message_date: str(c.last_message_date),
        unread: c.unread === true,
        message_type: str(c.message_type),
      }));

    return { conversations };
  } catch {
    return null;
  }
}
